import { defineStore } from 'pinia'
import { isRef } from 'vue'
import { storyblokService } from '@/services/storyblok.js'
import { isStoryblokEnabled } from '@/constants/storyblok.js'

export const useTranslationsStore = defineStore('translations', {
    state: () => ({
        locale: 'en',
        // True once the user picks a language manually, so the choice
        // survives reloads instead of being overridden by locale detection.
        localeOverridden: false,
        isLoaded: false,
        loadError: null,
        _i18n: null,
    }),

    persist: {
        paths: ['locale', 'localeOverridden'],
        serializer: {
            serialize: (state) =>
                JSON.stringify({ locale: state.locale, localeOverridden: state.localeOverridden }),
            deserialize: (str) => JSON.parse(str),
        },
    },

    getters: {
        // The locale actually shown. English-only while Storyblok is disabled;
        // a persisted choice is kept so it applies again once it's enabled.
        activeLocale: (state) => (isStoryblokEnabled ? state.locale : 'en'),
        canSwitchLocale: () => isStoryblokEnabled,
    },

    actions: {
        init(i18n) {
            this._i18n = i18n
        },

        t(key, params) {
            if (!this._i18n) return key
            return this._i18n.global.t(key, params)
        },

        async loadTranslations() {
            try {
                const messages = await storyblokService.fetchAllTranslations(this.locale)

                if (messages && this._i18n) {
                    this._i18n.global.setLocaleMessage(this.locale, messages)
                }
            } catch (error) {
                console.warn('Storyblok translations unavailable, using bundled fallback:', error)
                this.loadError = error.message
            } finally {
                this.isLoaded = true
                if (this._i18n) {
                    const loc = this._i18n.global.locale
                    if (isRef(loc)) {
                        loc.value = this.activeLocale
                    } else {
                        this._i18n.global.locale = this.activeLocale
                    }
                }
            }
        },

        async setLocale(locale) {
            this.locale = locale
            this.localeOverridden = true
            this.isLoaded = false
            this.loadError = null
            await this.loadTranslations()
        },
    },
})
